import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import SystemSettingsForm from '../components/SystemSettingsForm';
import ConfirmationModal from '../components/ConfirmationModal';
import { useToast } from '../hooks/useToast';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const API_PREFIX = '/internal/slicer-settings/global';

const TYPE_COLORS = {
  filament: 'bg-purple-100 text-purple-700',
  machine: 'bg-blue-100 text-blue-700',
  machine_model: 'bg-cyan-100 text-cyan-700',
  process: 'bg-green-100 text-green-700',
};

export default function AdminSlicerPresets() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [filters, setFilters] = useState({ search: '', type: 'all' });
  const bulkInputRef = useRef(null);
  const { showToast } = useToast();

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/auth/slicer-settings/base');
      // Only show global/system presets (user: null)
      const all = res.data?.data || [];
      setSettings(all.filter(s => !s.user));
    } catch {
      showToast('Failed to load presets', 'error');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  useEffect(() => { fetchSettings(); }, []);

  const filtered = settings
    .filter(s => {
      if (filters.search && !s.name?.toLowerCase().includes(filters.search.toLowerCase())) return false;
      if (filters.type !== 'all' && s.type?.toLowerCase() !== filters.type) return false;
      return true;
    })
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

  // Single upload via the existing SystemSettingsForm
  const handleSave = async (formData) => {
    const data = new FormData();
    data.append('file', formData.file);
    data.append('type', formData.type);
    if (formData.name) data.append('name', formData.name);
    if (formData.metadata && Object.keys(formData.metadata).length > 0) {
      data.append('metadata', JSON.stringify(formData.metadata));
    }
    if (formData.forceUpload) data.append('forceUpload', 'true');

    await api.post(API_PREFIX, data, { headers: { 'Content-Type': 'multipart/form-data' } });
    showToast('Global preset uploaded', 'success');
    setShowForm(false);
    fetchSettings();
  };

  // Bulk upload
  const handleBulkUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setBulkUploading(true);
    try {
      const data = new FormData();
      for (let i = 0; i < files.length; i++) {
        data.append('files', files[i]);
      }
      const res = await api.post(`${API_PREFIX}/bulk`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      const result = res.data?.data || {};
      const failCount = result.failed?.length || 0;
      showToast(`Imported ${result.imported || 0} presets${failCount ? `, ${failCount} failed` : ''}`, failCount ? 'warning' : 'success');
      fetchSettings();
    } catch (err) {
      showToast(err.response?.data?.message || 'Bulk upload failed', 'error');
    } finally {
      setBulkUploading(false);
      if (bulkInputRef.current) bulkInputRef.current.value = '';
    }
  };

  // Inline edit (name only for now)
  const handleEditSave = async () => {
    if (!editTarget) return;
    try {
      await api.patch(`${API_PREFIX}/${editTarget._id}`, { name: editTarget.name });
      showToast('Preset updated', 'success');
      setEditTarget(null);
      fetchSettings();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`${API_PREFIX}/${deleteTarget._id}`);
      showToast('Preset deleted', 'success');
      setDeleteTarget(null);
      fetchSettings();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  if (showForm) {
    return <SystemSettingsForm onSave={handleSave} onCancel={() => setShowForm(false)} />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="System Slicer Presets"
        description={`Manage global slicer presets visible to all operators — ${filtered.length} preset${filtered.length !== 1 ? 's' : ''}`}
        actions={
          <div className="flex gap-2">
            <label className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg cursor-pointer transition-colors ${bulkUploading ? 'bg-slate-300 text-slate-500' : 'bg-amber-600 hover:bg-amber-700 text-white'}`}>
              <ArrowUpTrayIcon className="h-4 w-4" />
              {bulkUploading ? 'Uploading...' : 'Bulk Import'}
              <input
                ref={bulkInputRef}
                type="file"
                accept=".json"
                multiple
                className="hidden"
                disabled={bulkUploading}
                onChange={handleBulkUpload}
              />
            </label>
            <Button icon={PlusIcon} onClick={() => setShowForm(true)}>Upload Single</Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search presets..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={filters.type}
          onChange={(e) => setFilters(f => ({ ...f, type: e.target.value }))}
          className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
        >
          <option value="all">All Types</option>
          <option value="filament">Filament</option>
          <option value="machine">Machine</option>
          <option value="machine_model">Machine Model</option>
          <option value="process">Process</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Instantiation</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Updated</th>
              <th className="px-5 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-slate-500">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-slate-500">No system presets found</td></tr>
            ) : (
              filtered.map((s) => (
                <tr key={s._id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3 text-sm font-medium text-slate-900">{s.name}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[s.type] || 'bg-slate-100 text-slate-700'}`}>
                      {s.type}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {s.instantiation ? <Badge variant="success" size="sm">Yes</Badge> : <Badge variant="default" size="sm">No</Badge>}
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-500">{s.updatedAt ? new Date(s.updatedAt).toLocaleDateString() : '—'}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setEditTarget({ ...s })} className="p-1.5 text-slate-400 hover:text-blue-600 rounded transition-colors" title="Edit">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => setDeleteTarget(s)} className="p-1.5 text-slate-400 hover:text-red-600 rounded transition-colors" title="Delete">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit modal (inline name edit) */}
      {editTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditTarget(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900">Edit Preset</h3>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
              <input
                type="text"
                value={editTarget.name || ''}
                onChange={e => setEditTarget(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setEditTarget(null)} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={handleEditSave} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Save</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmationModal
          title="Delete System Preset"
          message={`Are you sure you want to delete "${deleteTarget.name}"? This will deactivate it for all users.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
